import React from 'react';
import {
	Box,
	Divider,
	Grid,
	GlobalStyles,
	Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PageWrapper from 'components/Common/PageWraper';
import { CustomizedDataGrid } from 'components/customized_data_grid';
import { Column } from 'react-data-grid';

// ─── PRE_AOP 2027 COST SUMMARY ─────────────────────────────────────────────

interface CostSummaryRow {
	id: string;
	program: string;
	fy2025Actuals: string;
	fy2026_6plus6: string;
	fy2027Baseline: string;
	newIncrement2027: string;
	requestedAOP2027: string;
	isParent?: boolean;
}

const customFormatter = (key: keyof CostSummaryRow) => ({ row }: { row: CostSummaryRow }) => {
	const isBold = row.isParent;
	return (
		<span style={{ fontWeight: isBold ? 600 : 400 }}>
			{row[key]}
		</span>
	);
};

const costSummaryColumns: Column<CostSummaryRow>[] = [
	{
		key: 'program',
		name: 'Program',
		minWidth: 280,
		frozen: true,
		formatter: customFormatter('program'),
	},
	{
		key: 'fy2025Actuals',
		name: 'FY 2025 (Actuals)',
		minWidth: 150,
		formatter: customFormatter('fy2025Actuals'),
	},
	{
		key: 'fy2026_6plus6',
		name: 'FY 2026 (6+6)',
		minWidth: 150,
		formatter: customFormatter('fy2026_6plus6'),
	},
	{
		key: 'fy2027Baseline',
		name: 'FY 2027 Baseline',
		minWidth: 160,
		formatter: customFormatter('fy2027Baseline'),
	},
	{
		key: 'newIncrement2027',
		name: '2027 New Increment',
		minWidth: 170,
		formatter: customFormatter('newIncrement2027'),
	},
	{
		key: 'requestedAOP2027',
		name: '2027 Requested AOP',
		minWidth: 180,
		formatter: customFormatter('requestedAOP2027'),
	},
];

const costSummaryData: CostSummaryRow[] = [
	{ id: '1',  program: 'EDF',                                          fy2025Actuals: '23.1', fy2026_6plus6: '26.1', fy2027Baseline: '26.1', newIncrement2027: '2.0',  requestedAOP2027: '28.1' },
	{ id: '2',  program: 'MDIP',                                         fy2025Actuals: '6.4',  fy2026_6plus6: '16.0', fy2027Baseline: '-*',   newIncrement2027: '-',    requestedAOP2027: '-'    },
	{ id: '3',  program: 'Global Programs',                              fy2025Actuals: '13.0', fy2026_6plus6: '17.4', fy2027Baseline: '17.4', newIncrement2027: '2.9',  requestedAOP2027: '20.3', isParent: true },
	{ id: '4',  program: 'IBP',                                          fy2025Actuals: '4.5',  fy2026_6plus6: '6.9',  fy2027Baseline: '6.9',  newIncrement2027: '2.2',  requestedAOP2027: '9.1'  },
	{ id: '5',  program: 'Mosaic (incl Mosaic AI)',                      fy2025Actuals: '5.5',  fy2026_6plus6: '5.3',  fy2027Baseline: '5.3',  newIncrement2027: '0.7',  requestedAOP2027: '6'    },
	{ id: '6',  program: 'Commercial & Consumer',                        fy2025Actuals: '1.2',  fy2026_6plus6: '1.8',  fy2027Baseline: '1.8',  newIncrement2027: '',     requestedAOP2027: ''     },
	{ id: '7',  program: 'Supply Chain',                                 fy2025Actuals: '0.8',  fy2026_6plus6: '1.9',  fy2027Baseline: '1.9',  newIncrement2027: '',     requestedAOP2027: ''     },
	{ id: '8',  program: 'Global Functions (ESG, Global Agro, Climate data)', fy2025Actuals: '1.1', fy2026_6plus6: '1.5', fy2027Baseline: '1.5', newIncrement2027: '', requestedAOP2027: '' },
	{ id: '9',  program: 'Sector Programs',                              fy2025Actuals: '27.5', fy2026_6plus6: '18.1', fy2027Baseline: '34.0', newIncrement2027: '2.6',  requestedAOP2027: '36.6', isParent: true },
	{ id: '10', program: 'PBNA',                                         fy2025Actuals: '5.6',  fy2026_6plus6: '3.8',  fy2027Baseline: '8.8',  newIncrement2027: '1.1',  requestedAOP2027: '10.0' },
	{ id: '11', program: 'PFNA',                                         fy2025Actuals: '11.7', fy2026_6plus6: '7.2',  fy2027Baseline: '10.7', newIncrement2027: '1.4',  requestedAOP2027: '12.2' },
	{ id: '12', program: 'LATAM',                                        fy2025Actuals: '2.5',  fy2026_6plus6: '2.1',  fy2027Baseline: '3.6',  newIncrement2027: '',     requestedAOP2027: '3.6'  },
	{ id: '13', program: 'Europe',                                       fy2025Actuals: '1.3',  fy2026_6plus6: '1.3',  fy2027Baseline: '2.0',  newIncrement2027: '',     requestedAOP2027: '2.0'  },
	{ id: '14', program: 'AMESA & APAC',                                 fy2025Actuals: '2.2',  fy2026_6plus6: '2.2',  fy2027Baseline: '4.0',  newIncrement2027: '',     requestedAOP2027: '4.0'  },
	{ id: '15', program: 'CGF',                                          fy2025Actuals: '4.2',  fy2026_6plus6: '1.5',  fy2027Baseline: '4.9',  newIncrement2027: '',     requestedAOP2027: '4.9'  },
	{ id: '16', program: 'AI Platforms',                                 fy2025Actuals: '11.2', fy2026_6plus6: '14.5', fy2027Baseline: '14.5', newIncrement2027: '4.8',  requestedAOP2027: '19.3', isParent: true },
	{ id: '17', program: 'AI Platforms - Azure',                         fy2025Actuals: '9.0',  fy2026_6plus6: '12.0', fy2027Baseline: '12.0', newIncrement2027: '3.5',  requestedAOP2027: '15.5' },
	{ id: '18', program: 'AI Platforms - AWS',                           fy2025Actuals: '2.2',  fy2026_6plus6: '2.5',  fy2027Baseline: '2.5',  newIncrement2027: '1.3',  requestedAOP2027: '3.8'  },
	{ id: '19', program: 'GCP',                                          fy2025Actuals: '0.5',  fy2026_6plus6: '0.3',  fy2027Baseline: '0.3',  newIncrement2027: '0.2',  requestedAOP2027: '0.5' },
	{ id: '20', program: 'PepWise',                                      fy2025Actuals: '1.2',  fy2026_6plus6: '1.7',  fy2027Baseline: '1.7',  newIncrement2027: '',     requestedAOP2027: '1.7' },
	{ id: '21', program: 'Integration Platforms (Azure+AWS)',             fy2025Actuals: '4.7',  fy2026_6plus6: '2.5',  fy2027Baseline: '2.5',  newIncrement2027: '',     requestedAOP2027: '2.5' },
	{ id: '22', program: 'COST TOTAL',                            fy2025Actuals: '87.5', fy2026_6plus6: '96.5', fy2027Baseline: '96.5', newIncrement2027: '12.5', requestedAOP2027: '109.0', isParent: true },
];

const defaultCostSummaryColumns = costSummaryColumns.map((c) => c.name as string);

// ─── Component ────────────────────────────────────────────────────────────────
import RowTypography from 'components/Table/RowTypography';

/* ─── Consumption Metrics Types & Data ───────────────────────────── */

interface ConsumptionMetricRow {
	id: number;
	metric: string;
	fy2025: string;
	fy2026Baseline: string;
	fy2027NewIncrement: string;
	fy2027RequestedAop: string;
}

const renderTableValue = (value: string | number, fontWeight = 500) => (
	<RowTypography
		sx={{
			fontWeight,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
		}}
	>
		{value}
	</RowTypography>
);

const consumptionMetricColumns: Array<Column<ConsumptionMetricRow>> = [
	{
		key: 'metric',
		name: 'CONSUMPTION METRICS',
		resizable: true,
		minWidth: 220,
		formatter: ({ row }) => renderTableValue(row.metric, 600),
	},
	{
		key: 'fy2025',
		name: 'FY 2025 (Actuals)',
		resizable: true,
		minWidth: 160,
		formatter: ({ row }) => renderTableValue(row.fy2025),
	},
	{
		key: 'fy2026Baseline',
		name: 'FY 2026 (6+6) / FY 2027 Baseline',
		resizable: true,
		minWidth: 260,
		formatter: ({ row }) => renderTableValue(row.fy2026Baseline),
	},
	{
		key: 'fy2027NewIncrement',
		name: '2027 New Increment',
		resizable: true,
		minWidth: 180,
		formatter: ({ row }) => renderTableValue(row.fy2027NewIncrement),
	},
	{
		key: 'fy2027RequestedAop',
		name: '2027 Requested AOP',
		resizable: true,
		minWidth: 180,
		formatter: ({ row }) => renderTableValue(row.fy2027RequestedAop),
	},
];

const consumptionMetricDefaultColumnNames = [
	'CONSUMPTION METRICS',
	'FY 2025 (Actuals)',
	'FY 2026 (6+6) / FY 2027 Baseline',
	'2027 New Increment',
	'2027 Requested AOP',
];

const consumptionMetricTableData: ConsumptionMetricRow[] = [
	{
		id: 1,
		metric: '# PIPELINES',
		fy2025: '52,016',
		fy2026Baseline: '71635',
		fy2027NewIncrement: '8000',
		fy2027RequestedAop: '79635',
	},
	{
		id: 2,
		metric: 'DBU (million)',
		fy2025: '42.17',
		fy2026Baseline: '108.1',
		fy2027NewIncrement: '10.0',
		fy2027RequestedAop: '118.1',
	},
	{
		id: 3,
		metric: 'STORAGE (PB)',
		fy2025: '39PB',
		fy2026Baseline: '51PB',
		fy2027NewIncrement: '6PB',
		fy2027RequestedAop: '57PB',
	},
	{
		id: 4,
		metric: 'VM Usage (M hours)',
		fy2025: '58.4',
		fy2026Baseline: '66.0',
		fy2027NewIncrement: '3.0',
		fy2027RequestedAop: '69.0',
	},
];

/* ─── Pre AOP 2027 Details Types & Data ──────────────────────────── */

interface PreAop2027DetailRow {
	id: number;
	program: string;
	fy2025: string;
	fy2026: string;
	lever: string;
	baseline: string;
	increment: string;
	reason: string;
	aop: string;
	rowType: 'groupHeader' | 'subProgram' | 'metric' | 'total';
	groupId: number;
	isGroupFirst: boolean;
	isGroupLast: boolean;
}

const renderDetailValue = (
	value: string,
	rowType: PreAop2027DetailRow['rowType'],
	columnKey: keyof PreAop2027DetailRow,
	isGroupFirst?: boolean,
	groupId?: number,
) => {
	const isHeader = rowType === 'groupHeader';
	const isTotal = rowType === 'total';
	const isSubProgram = rowType === 'subProgram';

	const fontWeight = isHeader || isTotal ? 700 : isSubProgram ? 600 : 400;
	const fontStyle = isSubProgram ? 'italic' : 'normal';

	if ((columnKey === 'lever' || columnKey === 'reason') && !isGroupFirst) {
		return null;
	}

	const isLeverOrReason = columnKey === 'lever' || columnKey === 'reason';
	const groupCount = groupId ? preAop2027DetailTableData.filter((r) => r.groupId === groupId).length : 1;
	const cellHeight = isLeverOrReason && isGroupFirst ? `${groupCount * 40}px` : undefined;

	return (
		<RowTypography
			sx={{
				fontWeight,
				fontStyle,
				color: isTotal ? '#FFFFFF !important' : undefined,
				overflow: 'visible',
				whiteSpace: 'normal',
				wordBreak: 'break-word',
				paddingLeft: isSubProgram && columnKey === 'program' ? '12px' : '0px',
				fontSize: '14px',
				lineHeight: 1.35,
				...(isLeverOrReason
					? {
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'center',
							textAlign: 'center',
							height: cellHeight,
							width: '100%',
					  }
					: {
							textAlign: 'left',
					  }),
			}}
		>
			{value}
		</RowTypography>
	);
};

const getLeverCellClass = (row: PreAop2027DetailRow) => {
	let cls = 'merged-lever-cell';
	if (row.rowType === 'total') cls += ' merged-total';
	if (row.isGroupFirst) cls += ' merged-first';
	if (row.isGroupLast) cls += ' merged-last';
	return cls;
};

const getNormalCellClass = (row: PreAop2027DetailRow) => {
	if (row.rowType === 'groupHeader') return 'pre-aop-group-header-cell';
	if (row.rowType === 'total') return 'pre-aop-total-cell';
	return undefined;
};

const preAop2027DetailColumns: Array<Column<PreAop2027DetailRow>> = [
	{
		key: 'program',
		name: 'Program',
		resizable: true,
		minWidth: 200,
		cellClass: getNormalCellClass,
		formatter: ({ row }) => renderDetailValue(row.program, row.rowType, 'program'),
	},
	{
		key: 'fy2025',
		name: 'FY 2025 (Actuals)',
		resizable: true,
		minWidth: 140,
		cellClass: getNormalCellClass,
		formatter: ({ row }) => renderDetailValue(row.fy2025, row.rowType, 'fy2025'),
	},
	{
		key: 'fy2026',
		name: 'FY 2026 (6+6)',
		resizable: true,
		minWidth: 140,
		cellClass: getNormalCellClass,
		formatter: ({ row }) => renderDetailValue(row.fy2026, row.rowType, 'fy2026'),
	},
	{
		key: 'lever',
		name: 'FY26 FinOps Levers',
		resizable: true,
		minWidth: 280,
		width: 300,
		cellClass: getLeverCellClass,
		formatter: ({ row }) => renderDetailValue(row.lever, row.rowType, 'lever', row.isGroupFirst, row.groupId),
	},
	{
		key: 'baseline',
		name: '2027 Baseline',
		resizable: true,
		minWidth: 140,
		cellClass: getNormalCellClass,
		formatter: ({ row }) => renderDetailValue(row.baseline, row.rowType, 'baseline'),
	},
	{
		key: 'increment',
		name: '2027 New Increment',
		resizable: true,
		minWidth: 150,
		cellClass: getNormalCellClass,
		formatter: ({ row }) => renderDetailValue(row.increment, row.rowType, 'increment'),
	},
	{
		key: 'reason',
		name: 'Reason for Increment',
		resizable: true,
		minWidth: 300,
		cellClass: getLeverCellClass,
		formatter: ({ row }) => renderDetailValue(row.reason, row.rowType, 'reason', row.isGroupFirst, row.groupId),
	},
	{
		key: 'aop',
		name: '2027 Requested AOP',
		resizable: true,
		minWidth: 200,
		cellClass: getNormalCellClass,
		formatter: ({ row }) => renderDetailValue(row.aop, row.rowType, 'aop'),
	},
];

const preAop2027DetailDefaultColumnNames = [
	'Program',
	'FY 2025 (Actuals)',
	'FY 2026 (6+6)',
	'FY26 FinOps Levers',
	'2027 Baseline',
	'2027 New Increment',
	'Reason for Increment',
	'2027 Requested AOP',
];

const preAop2027DetailTableData: PreAop2027DetailRow[] = [
	// 1. Other Global Programs
	{ id: 1, program: 'Other Global Programs', fy2025: '4.3', fy2026: '6.2', lever: '~$1.2M savings expected in 2026 through Cluster & job right-sizing, VM decommissioning, SQL DB cleanup, RIs', baseline: '6.2', increment: '-', reason: '', aop: '6.2', rowType: 'groupHeader', groupId: 1, isGroupFirst: true, isGroupLast: false },
	{ id: 2, program: 'Mosaic AI', fy2025: '1.2', fy2026: '1.0', lever: '', baseline: '1.0', increment: '-', reason: '', aop: '1.0', rowType: 'subProgram', groupId: 1, isGroupFirst: false, isGroupLast: false },
	{ id: 3, program: 'Commercial & Consumer', fy2025: '1.2', fy2026: '1.8', lever: '', baseline: '1.8', increment: '-', reason: '', aop: '1.8', rowType: 'subProgram', groupId: 1, isGroupFirst: false, isGroupLast: false },
	{ id: 4, program: 'Supply Chain', fy2025: '0.8', fy2026: '1.9', lever: '', baseline: '1.9', increment: '-', reason: '', aop: '1.9', rowType: 'subProgram', groupId: 1, isGroupFirst: false, isGroupLast: false },
	{ id: 5, program: 'Global Functions', fy2025: '1.1', fy2026: '1.5', lever: '', baseline: '1.5', increment: '-', reason: '', aop: '1.5', rowType: 'subProgram', groupId: 1, isGroupFirst: false, isGroupLast: false },
	{ id: 6, program: '# Pipelines', fy2025: '3740', fy2026: '5093', lever: '', baseline: '5093', increment: '-', reason: '', aop: '5093', rowType: 'metric', groupId: 1, isGroupFirst: false, isGroupLast: false },
	{ id: 7, program: 'DBUs (M)', fy2025: '3.398', fy2026: '7.168', lever: '', baseline: '7.168', increment: '-', reason: '', aop: '7.168', rowType: 'metric', groupId: 1, isGroupFirst: false, isGroupLast: false },
	{ id: 8, program: 'Storage (PB)', fy2025: '2.563', fy2026: '3.599', lever: '', baseline: '3.599', increment: '-', reason: '', aop: '3.599', rowType: 'metric', groupId: 1, isGroupFirst: false, isGroupLast: false },
	{ id: 9, program: 'VM usage (M hours)', fy2025: '2.458', fy2026: '2.713', lever: '', baseline: '2.713', increment: '-', reason: '', aop: '2.713', rowType: 'metric', groupId: 1, isGroupFirst: false, isGroupLast: true },

	// 2. Other Sector Programs
	{ id: 10, program: 'Other Sector Programs', fy2025: '10.2', fy2026: '7.1', lever: '~$3M savings expected in 2026 through Cluster & VM right-sizing, ADF cleanup, RIs & Savings', baseline: '14.5', increment: '-', reason: '', aop: '14.5', rowType: 'groupHeader', groupId: 2, isGroupFirst: true, isGroupLast: false },
	{ id: 11, program: 'LATAM', fy2025: '2.5', fy2026: '2.1', lever: '', baseline: '3.6', increment: '-', reason: '', aop: '3.6', rowType: 'subProgram', groupId: 2, isGroupFirst: false, isGroupLast: false },
	{ id: 12, program: 'Europe', fy2025: '1.3', fy2026: '1.3', lever: '', baseline: '2.0', increment: '-', reason: '', aop: '2.0', rowType: 'subProgram', groupId: 2, isGroupFirst: false, isGroupLast: false },
	{ id: 13, program: 'AMESA & APAC', fy2025: '2.2', fy2026: '2.2', lever: '', baseline: '4.0', increment: '-', reason: '', aop: '4.0', rowType: 'subProgram', groupId: 2, isGroupFirst: false, isGroupLast: false },
	{ id: 14, program: 'CGF', fy2025: '4.2', fy2026: '1.5', lever: '', baseline: '4.9', increment: '-', reason: '', aop: '4.9', rowType: 'subProgram', groupId: 2, isGroupFirst: false, isGroupLast: false },
	{ id: 15, program: '# Pipelines', fy2025: '4283', fy2026: '5832', lever: '', baseline: '10988', increment: '-', reason: '', aop: '10988', rowType: 'metric', groupId: 2, isGroupFirst: false, isGroupLast: false },
	{ id: 16, program: 'DBUs (M)', fy2025: '3.233', fy2026: '6.823', lever: '', baseline: '14.048', increment: '-', reason: '', aop: '14.048', rowType: 'metric', groupId: 2, isGroupFirst: false, isGroupLast: false },
	{ id: 17, program: 'Storage (PB)', fy2025: '5.207', fy2026: '7.315', lever: '', baseline: '13.119', increment: '-', reason: '', aop: '13.119', rowType: 'metric', groupId: 2, isGroupFirst: false, isGroupLast: false },
	{ id: 18, program: 'VM usage (M hours)', fy2025: '6.416', fy2026: '7.081', lever: '', baseline: '11.579', increment: '-', reason: '', aop: '11.579', rowType: 'metric', groupId: 2, isGroupFirst: false, isGroupLast: true },

	// 3. MDIP*
	{ id: 19, program: 'MDIP*', fy2025: '6.4', fy2026: '16', lever: 'Optimizations planned: LCM, cluster right-sizing, SQL DB cleanup', baseline: '-*', increment: '-', reason: '', aop: '-', rowType: 'groupHeader', groupId: 3, isGroupFirst: true, isGroupLast: false },
	{ id: 20, program: '# Pipelines', fy2025: '9649', fy2026: '13143', lever: '', baseline: '-', increment: '-', reason: '', aop: '', rowType: 'metric', groupId: 3, isGroupFirst: false, isGroupLast: false },
	{ id: 21, program: 'DBUs (M)', fy2025: '8.73', fy2026: '18.419', lever: '', baseline: '-', increment: '-', reason: '', aop: '', rowType: 'metric', groupId: 3, isGroupFirst: false, isGroupLast: false },
	{ id: 22, program: 'Storage (PB)', fy2025: '10.533', fy2026: '14.796', lever: '', baseline: '-', increment: '-', reason: '', aop: '', rowType: 'metric', groupId: 3, isGroupFirst: false, isGroupLast: false },
	{ id: 23, program: 'VM usage (M hours)', fy2025: '10.389', fy2026: '11.466', lever: '', baseline: '-', increment: '-', reason: '', aop: '', rowType: 'metric', groupId: 3, isGroupFirst: false, isGroupLast: true },

	// 4. Other Programs
	{ id: 24, program: 'Other Programs', fy2025: '5.3', fy2026: '4.1', lever: 'Optimizations planned: RIs, SQL DBs cleanup, cluster right-sizing', baseline: '4.1', increment: '-', reason: '', aop: '4.1', rowType: 'groupHeader', groupId: 4, isGroupFirst: true, isGroupLast: false },
	{ id: 25, program: 'PepWise', fy2025: '1.2', fy2026: '1.7', lever: '', baseline: '1.7', increment: '-', reason: '', aop: '1.7', rowType: 'subProgram', groupId: 4, isGroupFirst: false, isGroupLast: false },
	{ id: 26, program: 'Integration Platforms', fy2025: '4.1', fy2026: '2.4', lever: '', baseline: '2.4', increment: '-', reason: '', aop: '2.4', rowType: 'subProgram', groupId: 4, isGroupFirst: false, isGroupLast: false },
	{ id: 27, program: '# Pipelines', fy2025: '2472', fy2026: '3367', lever: '', baseline: '3367', increment: '-', reason: '', aop: '3367', rowType: 'metric', groupId: 4, isGroupFirst: false, isGroupLast: false },
	{ id: 28, program: 'DBUs (M)', fy2025: '0.318', fy2026: '0.671', lever: '', baseline: '0.671', increment: '-', reason: '', aop: '0.671', rowType: 'metric', groupId: 4, isGroupFirst: false, isGroupLast: false },
	{ id: 29, program: 'Storage (PB)', fy2025: '0.964', fy2026: '1.353', lever: '', baseline: '1.353', increment: '-', reason: '', aop: '1.353', rowType: 'metric', groupId: 4, isGroupFirst: false, isGroupLast: false },
	{ id: 30, program: 'VM usage (M hours)', fy2025: '6.216', fy2026: '6.859', lever: '', baseline: '6.859', increment: '-', reason: '', aop: '6.859', rowType: 'metric', groupId: 4, isGroupFirst: false, isGroupLast: true },

	// 5. AWS Platforms
	{ id: 31, program: 'AWS Platforms', fy2025: '0.6', fy2026: '0.1', lever: '', baseline: '0.1', increment: '-', reason: '', aop: '0.1', rowType: 'groupHeader', groupId: 5, isGroupFirst: true, isGroupLast: true },

	// 6. GCP
	{ id: 32, program: 'GCP', fy2025: '0.5', fy2026: '0.3', lever: '-', baseline: '0.3', increment: '0.2', reason: '', aop: '0.5', rowType: 'groupHeader', groupId: 6, isGroupFirst: true, isGroupLast: true },

	// 7. EDF
	{ id: 33, program: 'EDF', fy2025: '23.1', fy2026: '26.1', lever: '~$2M savings expected in 2026 through ADF trigger cleanup, VM/disk cleanup, SQL right-sizing, storage lifecycle, RIs & Savings Plans', baseline: '26.1', increment: '2.0', reason: 'New increment of $2M is on account of further Sector Data lake migrations', aop: '28.1', rowType: 'groupHeader', groupId: 7, isGroupFirst: true, isGroupLast: false },
	{ id: 34, program: '# Pipelines', fy2025: '10762', fy2026: '14800', lever: '', baseline: '14800', increment: '2100', reason: '', aop: '16900', rowType: 'metric', groupId: 7, isGroupFirst: false, isGroupLast: false },
	{ id: 35, program: 'DBUs (M)', fy2025: '9.29', fy2026: '29.02', lever: '', baseline: '29.02', increment: '3.6', reason: '', aop: '32.62', rowType: 'metric', groupId: 7, isGroupFirst: false, isGroupLast: false },
	{ id: 36, program: 'Storage (PB)', fy2025: '13.532', fy2026: '15.951', lever: '', baseline: '15.951', increment: '3', reason: '', aop: '18.951', rowType: 'metric', groupId: 7, isGroupFirst: false, isGroupLast: false },
	{ id: 37, program: 'VM usage (M hours)', fy2025: '14', fy2026: '19', lever: '', baseline: '19', increment: '1.4', reason: '', aop: '20.4', rowType: 'metric', groupId: 7, isGroupFirst: false, isGroupLast: true },

	// 8. IBP
	{ id: 38, program: 'IBP', fy2025: '4.5', fy2026: '6.9', lever: '~$0.3M savings expected in 2026 through ADF non-prod trigger cleanup & high-failure pipeline remediation, Databricks cluster right-sizing', baseline: '6.9', increment: '2.2', reason: 'New increment of 2.2M is based on new markets onboarding increasing the DFUs (%) as follows: UK/Poland (41%); Brazil (10%), FLNA Quaker (11%), PBNA Gatorade(7%), ANZ (11%), CH (15%), SA (12%)', aop: '9.1', rowType: 'groupHeader', groupId: 8, isGroupFirst: true, isGroupLast: false },
	{ id: 39, program: '# Pipelines', fy2025: '4788', fy2026: '7000', lever: '', baseline: '7000', increment: '1500', reason: '', aop: '8500', rowType: 'metric', groupId: 8, isGroupFirst: false, isGroupLast: false },
	{ id: 40, program: 'DBUs (M)', fy2025: '4.26', fy2026: '18.49', lever: '', baseline: '18.49', increment: '2.4', reason: '', aop: '20.89', rowType: 'metric', groupId: 8, isGroupFirst: false, isGroupLast: false },
	{ id: 41, program: 'Storage (PB)', fy2025: '0.61', fy2026: '0.84', lever: '', baseline: '0.84', increment: '0.1', reason: '', aop: '0.94', rowType: 'metric', groupId: 8, isGroupFirst: false, isGroupLast: false },
	{ id: 42, program: 'VM usage (M hours)', fy2025: '0', fy2026: '0', lever: '', baseline: '0', increment: '', reason: '', aop: '0', rowType: 'metric', groupId: 8, isGroupFirst: false, isGroupLast: true },

	// 9. Mosaic
	{ id: 43, program: 'Mosaic', fy2025: '4.3', fy2026: '4.3', lever: '~$0.5M savings expected in 2026 through ADF non-prod trigger cleanup, high-failure-rate pipeline remediation, idle/underutilized VM decommissioning, Reserved RI, & Databricks cluster right-sizing', baseline: '4.3', increment: '0.7', reason: 'New Increment of 0.7M is based on New Market Onboarding: Pakistan, China, PBF', aop: '5', rowType: 'groupHeader', groupId: 9, isGroupFirst: true, isGroupLast: false },
	{ id: 44, program: '# Pipelines', fy2025: '4162', fy2026: '5700', lever: '', baseline: '5700', increment: '700', reason: '', aop: '6400', rowType: 'metric', groupId: 9, isGroupFirst: false, isGroupLast: false },
	{ id: 45, program: 'DBUs (M)', fy2025: '3.06', fy2026: '10.94', lever: '', baseline: '10.94', increment: '1.6', reason: '', aop: '12.54', rowType: 'metric', groupId: 9, isGroupFirst: false, isGroupLast: false },
	{ id: 46, program: 'Storage (PB)', fy2025: '0.008', fy2026: '0.015', lever: '', baseline: '0.015', increment: '0.005', reason: '', aop: '0.02', rowType: 'metric', groupId: 9, isGroupFirst: false, isGroupLast: false },
	{ id: 47, program: 'VM usage (M hours)', fy2025: '0.55', fy2026: '0.58', lever: '', baseline: '0.58', increment: '0.05', reason: '', aop: '0.63', rowType: 'metric', groupId: 9, isGroupFirst: false, isGroupLast: true },

	// 10. NA Sector Programs
	{ id: 48, program: 'NA Sector Programs', fy2025: '17.3', fy2026: '11', lever: '~6M Savings expected in 2026 through ADF pipeline hygiene (non-prod triggers + high-failure remediation), VM & SQL right-sizing, RIs/Savings Plans, right-sizing idle VMs, LCM, and Databricks cluster right-sizing.', baseline: '19.5', increment: '2.5', reason: 'New Increment of 2.5M is based on One NA Agenda, MDIP/Gold Data Consumption Chargeback cost.', aop: '22.1', rowType: 'groupHeader', groupId: 10, isGroupFirst: true, isGroupLast: false },
	{ id: 49, program: 'PBNA', fy2025: '5.6', fy2026: '3.8', lever: '', baseline: '8.8', increment: '1.1', reason: '', aop: '10', rowType: 'subProgram', groupId: 10, isGroupFirst: false, isGroupLast: false },
	{ id: 50, program: 'PFNA', fy2025: '11.7', fy2026: '7.2', lever: '', baseline: '10.7', increment: '1.4', reason: '', aop: '12.2', rowType: 'subProgram', groupId: 10, isGroupFirst: false, isGroupLast: false },
	{ id: 51, program: '# Pipelines', fy2025: '12160', fy2026: '16700', lever: '', baseline: '24687.459', increment: '3700', reason: '', aop: '28387.459', rowType: 'metric', groupId: 10, isGroupFirst: false, isGroupLast: false },
	{ id: 52, program: 'DBUs (M)', fy2025: '9.88', fy2026: '16.57', lever: '', baseline: '27.764', increment: '2.4', reason: '', aop: '30.164', rowType: 'metric', groupId: 10, isGroupFirst: false, isGroupLast: false },
	{ id: 53, program: 'Storage (PB)', fy2025: '5.583', fy2026: '6.6', lever: '', baseline: '15.592', increment: '3', reason: '', aop: '18.592', rowType: 'metric', groupId: 10, isGroupFirst: false, isGroupLast: false },
	{ id: 54, program: 'VM usage (M hours)', fy2025: '11.4', fy2026: '8', lever: '', baseline: '14.968', increment: '1.5', reason: '', aop: '16.468', rowType: 'metric', groupId: 10, isGroupFirst: false, isGroupLast: true },

	// 11. AI Platforms
	{ id: 55, program: 'AI Platforms', fy2025: '11.2', fy2026: '14.5', lever: 'Optimizations targeted: AI token governance & CloudWatch based AWS Optimizations', baseline: '14.5', increment: '4.8', reason: 'New Increment of 4.8M is based on AI initiatives spanning PBNA ($1.2M), PFNA ($1.2M), Europe ($0.6M), CGF ($0.6M), Commercial & Consumer ($0.4M), Supply Chain ($0.4M), LATAM ($0.3M), and AMESA & APAC ($0.3M) across PepGenX, PepVigil, DevX, Agent scale up and AI for BI.', aop: '19.3', rowType: 'groupHeader', groupId: 11, isGroupFirst: true, isGroupLast: false },
	{ id: 56, program: 'AI Platforms - Azure', fy2025: '9', fy2026: '12', lever: '', baseline: '12', increment: '3.5', reason: '', aop: '15.5', rowType: 'subProgram', groupId: 11, isGroupFirst: false, isGroupLast: false },
	{ id: 57, program: 'AI Platforms - AWS', fy2025: '2.2', fy2026: '2.5', lever: '', baseline: '2.5', increment: '1.3', reason: '', aop: '3.8', rowType: 'subProgram', groupId: 11, isGroupFirst: false, isGroupLast: false },
	{ id: 58, program: 'Storage (PB)', fy2025: '0', fy2026: '0.53', lever: '', baseline: '0.53', increment: '-', reason: '', aop: '0.53', rowType: 'metric', groupId: 11, isGroupFirst: false, isGroupLast: false },
	{ id: 59, program: 'VM usage (M hours)', fy2025: '6.97', fy2026: '10.3', lever: '', baseline: '10.3', increment: '2.5', reason: '', aop: '12.8', rowType: 'metric', groupId: 11, isGroupFirst: false, isGroupLast: true },

	// 12. TOTAL
	{ id: 60, program: 'TOTAL', fy2025: '87.5', fy2026: '96.5', lever: '~$13M savings expected in 2026 through various optimization levers', baseline: '96.5', increment: '12.4', reason: '', aop: '109', rowType: 'total', groupId: 12, isGroupFirst: true, isGroupLast: false },
	{ id: 61, program: '# Pipelines', fy2025: '52016', fy2026: '71635', lever: '', baseline: '71635', increment: '8000', reason: '', aop: '79635', rowType: 'total', groupId: 12, isGroupFirst: false, isGroupLast: false },
	{ id: 62, program: 'DBUs (M)', fy2025: '42.17', fy2026: '108.1', lever: '', baseline: '108.1', increment: '10', reason: '', aop: '118.1', rowType: 'total', groupId: 12, isGroupFirst: false, isGroupLast: false },
	{ id: 63, program: 'Storage (PB)', fy2025: '39', fy2026: '51', lever: '', baseline: '51', increment: '6.1', reason: '', aop: '57.1', rowType: 'total', groupId: 12, isGroupFirst: false, isGroupLast: false },
	{ id: 64, program: 'VM usage (M hours)', fy2025: '58.4', fy2026: '66', lever: '', baseline: '66', increment: '5.45', reason: '', aop: '71.45', rowType: 'total', groupId: 12, isGroupFirst: false, isGroupLast: true },
];

const PreAop2027Report = () => {
	const theme = useTheme();
	const colors = theme.palette as any;

	return (
		<PageWrapper sx={{ mt: '64px', pt: '36px !important', px: '32px !important' }}>
			<GlobalStyles
				styles={{
					/* 1. Ensure table header stays on top of all body rows when scrolling */
					'.rdg-header-row, .rdg-header-row .rdg-cell, .rdg-cell[role="columnheader"]': {
						zIndex: '500 !important',
					},

					/* 2. Ensure group header row containing merged-first cell stays stacked above lower sub-rows */
					'.rdg-row:has(.merged-first)': {
						zIndex: '100 !important',
					},
					'.rdg-row:hover': {
						zIndex: '1 !important',
					},
					'.rdg-row:has(.merged-first):hover': {
						zIndex: '100 !important',
					},

					/* 2. Override CustomizedDataGrid default .rdg-cell:hover overflowY: hidden */
					'.rdg-cell.merged-lever-cell, .rdg-cell.merged-lever-cell:hover, .rdg-row:hover .rdg-cell.merged-lever-cell, .rdg-row:hover .rdg-cell.merged-lever-cell:hover': {
						overflow: 'visible !important',
						overflowX: 'visible !important',
						overflowY: 'visible !important',
						textOverflow: 'unset !important',
					},

					/* 3. Base merged cell styling */
					'.merged-lever-cell': {
						backgroundColor: '#EBF3FE !important',
						borderTop: 'none !important',
						borderBottom: 'none !important',
						boxShadow: 'inset 1px 0 0 #D0E2FA, inset -1px 0 0 #D0E2FA !important',
						textAlign: 'center !important',
					},
					'.rdg-row:hover .merged-lever-cell': {
						backgroundColor: '#EBF3FE !important',
					},

					/* 4. First cell in merged group holds text and has top border & highest z-index */
					'.merged-lever-cell.merged-first': {
						borderTop: '1px solid #B0C4DE !important',
						position: 'relative !important',
						zIndex: '105 !important',
					},

					/* 5. Sub-row cells in merged group pass pointer events so hovering doesn't obscure row 1 text */
					'.merged-lever-cell:not(.merged-first)': {
						pointerEvents: 'none !important',
					},

					/* 6. Last cell in merged group closes bottom border */
					'.merged-lever-cell.merged-last': {
						borderBottom: '1px solid #B0C4DE !important',
					},

					/* 7. Group headers & Total styling */
					'.pre-aop-group-header-cell, .rdg-row:hover .pre-aop-group-header-cell, .rdg-cell.pre-aop-group-header-cell:hover': {
						backgroundColor: '#D6E6F7 !important',
						fontWeight: '700 !important',
						color: '#003771 !important',
					},
					'.rdg-row:has(.pre-aop-total-cell) .rdg-cell, .rdg-row:has(.pre-aop-total-cell) .rdg-cell *, .rdg-row:has(.pre-aop-total-cell):hover .rdg-cell, .rdg-row:has(.pre-aop-total-cell):hover .rdg-cell *, .rdg-row:has(.pre-aop-total-cell) .rdg-cell:hover, .rdg-row:has(.pre-aop-total-cell) .rdg-cell:hover *, .pre-aop-total-cell, .pre-aop-total-cell *, .rdg-row:hover .pre-aop-total-cell, .rdg-row:hover .pre-aop-total-cell *, .rdg-cell.pre-aop-total-cell:hover, .rdg-cell.pre-aop-total-cell:hover *, .rdg-cell.pre-aop-total-cell[aria-selected="true"], .rdg-cell.pre-aop-total-cell[aria-selected="true"] *': {
						backgroundColor: '#003771 !important',
						fontWeight: '700 !important',
						color: '#FFFFFF !important',
					},
					'.rdg-row:has(.merged-total) .rdg-cell, .rdg-row:has(.merged-total) .rdg-cell *, .rdg-row:has(.merged-total):hover .rdg-cell, .rdg-row:has(.merged-total):hover .rdg-cell *, .merged-lever-cell.merged-total, .merged-lever-cell.merged-total *, .rdg-row:hover .merged-lever-cell.merged-total, .rdg-row:hover .merged-lever-cell.merged-total *, .rdg-cell.merged-lever-cell.merged-total:hover, .rdg-cell.merged-lever-cell.merged-total:hover *': {
						backgroundColor: '#003771 !important',
						color: '#FFFFFF !important',
						boxShadow: 'none !important',
					},
				}}
			/>

			{/* ─── Section 1: CONSUMPTION SUMMARY ───────────────────────────── */}
			<Typography
				variant="widgetTitleNew"
				mb={2}
				sx={{
					color: colors.widget.text.title,
					fontWeight: 600,
				}}
			>
				Cost Summary in $ Millions
			</Typography>

			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Box
						p={2}
						borderRadius="4px"
						sx={{ backgroundColor: `${colors.widget.background.card} !important` }}
					>
						<CustomizedDataGrid
							tableColumns={costSummaryColumns}
							tableData={costSummaryData}
							tableType="pre-aop-2027-cost-summary"
							defaultColumnNames={defaultCostSummaryColumns}
							isFetching={false}
							isRenderPagination={false}
							isShowMoreButton={false}
							autoSetDraggedColumns={false}
							childRowClass={true}
							isLighterTheme={true}
							style={{ height: '290px'}}
						/>
					</Box>
				</Grid>
			</Grid>

			<Divider
				sx={{
					my: 3,
					borderColor: colors.widget?.border?.main ?? '#AABBD4',
				}}
			/>

			{/* ─── Section 2: Consumption Metrics ───────────────────────────── */}
			<Typography
				variant="widgetTitleNew"
				mb={2}
				sx={{
					color: colors.widget.text.title,
					fontWeight: 600,
				}}
			>
				Consumption Metrics
			</Typography>

			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Box
						p={2}
						borderRadius="4px"
						sx={{ backgroundColor: `${colors.widget.background.card} !important` }}
					>
						<CustomizedDataGrid
							tableColumns={consumptionMetricColumns}
							tableData={consumptionMetricTableData}
							tableType="preAop2027ConsumptionMetrics"
							defaultColumnNames={consumptionMetricDefaultColumnNames}
							listLength={consumptionMetricTableData.length}
							isFetching={false}
							isRenderPagination={false}
							isShowMoreButton={false}
							autoSetDraggedColumns
							isLighterTheme
							rowHeight={38}
							headerRowHeight={42}
							isDateRangeFilter={false}
							isDisabled={false}
							showExport={false}
						/>
					</Box>
				</Grid>
			</Grid>

			<Divider
				sx={{
					my: 3,
					borderColor: colors.widget?.border?.main ?? '#AABBD4',
				}}
			/>

			{/* ─── Section 3: Pre AOP 2027 details ──────────────────────────── */}
			<Typography
				variant="widgetTitleNew"
				mb={2}
				sx={{
					color: colors.widget.text.title,
					fontWeight: 600,
				}}
			>
				Pre AOP 2027 Details in $ Millions
			</Typography>

			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Box
						p={2}
						borderRadius="4px"
						sx={{ backgroundColor: `${colors.widget.background.card} !important` }}
					>
						<CustomizedDataGrid
							tableColumns={preAop2027DetailColumns}
							tableData={preAop2027DetailTableData}
							tableType="preAop2027Details"
							defaultColumnNames={preAop2027DetailDefaultColumnNames}
							listLength={preAop2027DetailTableData.length}
							isFetching={false}
							isRenderPagination={false}
							isShowMoreButton={false}
							autoSetDraggedColumns
							isLighterTheme
							rowHeight={40}
							headerRowHeight={42}
							isDateRangeFilter={false}
							isDisabled={false}
							showExport={false}
						/>
					</Box>
				</Grid>
			</Grid>

		</PageWrapper>
	);
};

export default PreAop2027Report;
